import { Injectable } from "@angular/core";
import { MatDialog } from "@angular/material/dialog";
import { Router } from "@angular/router";
import moment from "moment";
import { NgxIndexedDBService } from "ngx-indexed-db";
import { CONSTANT } from "src/app/constant";
import { RouteType } from "src/app/models/enum";
import { DraftBody, DraftItem, RatingCandidate, TempDraftItem } from "src/app/models/models";
import { CbtaModalComponent } from "../components/cbta-modal/cbta-modal.component";
import { HttpService } from "./http.service";

@Injectable({
	providedIn: "root",
})
export class DraftService {
	private _inDraftMode: boolean = false;

	private _draft?: DraftItem;
	private _tempDraft?: Partial<TempDraftItem>;

	constructor(
		private dbService: NgxIndexedDBService,
		private httpService: HttpService,
		private dialog: MatDialog,
		private router: Router,
	) {}

	set inDraftMode(inDraftMode: boolean) {
		this._inDraftMode = inDraftMode;
	}

	get inDraftMode(): boolean {
		return this._inDraftMode;
	}

	get isSynced(): boolean {
		return this._draft?.synced ?? false;
	}

	get tempDraft(): Partial<TempDraftItem> {
		return this._tempDraft ?? {};
	}

	set tempDraft(draftItem: Partial<TempDraftItem>) {
		this._tempDraft = {
			...this._tempDraft,
			...draftItem,
		};
		console.log("tmp", this._tempDraft);
	}

	clearTempDraft() {
		this._tempDraft = {};
	}

	loadDraftItem(draftId: string) {
		this.dbService.getByID<DraftItem>(CONSTANT.INDEXEDDB.DRAFT, draftId).subscribe({
			next: (draft) => {
				this._draft = draft;
				this._inDraftMode = true;
			},
			error: console.error,
			complete: () => console.log("loadDraftItem {draftId} completed"),
		});
	}

	updateSelectedDraft(data: Partial<DraftItem>) {
		if (this._draft) {
			const draftId = this._draft?.id;
			if (draftId) {
				this.dbService.getByID<DraftItem>(CONSTANT.INDEXEDDB.DRAFT, draftId).subscribe({
					next: (record) => {
						this.dbService
							.update(CONSTANT.INDEXEDDB.DRAFT, {
								...record,
								...data,
								lastUpdatedDate: new Date().toISOString(),
							})
							.subscribe({
								next: (result) => {
									console.log("rating updated", result);

									this.loadDraftItem(draftId);
								},
								error: console.error,
							});
					},
					error: console.error,
					complete: () => console.log("loadDraftItem {draftId} completed"),
				});
			}
		}
	}

	deleteSelectedDraft() {
		if (this._draft) {
			const draftId = this._draft.id;

			if (draftId) {
				this.dbService.delete(CONSTANT.INDEXEDDB.DRAFT, draftId).subscribe((r) => {
					console.log("draft deleted!", r);
					this.clearDraft();
				});
			}
		}
	}

	deleteCandidateForSelectedDraft(idx: number) {
		if (this._draft) {
			const draftId = this._draft.id;

			if (draftId) {
				this.dbService.getByID<DraftItem>(CONSTANT.INDEXEDDB.DRAFT, draftId).subscribe((record) => {
					const formDetails = record?.formDetails;
					const trainingInfo = record?.trainingInformation;
					const candidateRating = record?.candidateRating;
					const signOffDetails = record?.signOffDetails;

					const assessmentIds = formDetails?.assessmentConfig.map((c) => c.assessmentId);

					const candidates = (trainingInfo?.candidates ?? []).sort((a, b) =>
						a.isPrimary === b.isPrimary ? 0 : a.isPrimary ? -1 : 1,
					);

					const candidateId = candidates[idx].candidateId;

					console.log(candidateId);

					if (candidateId) {
						const filteredCandidates = candidates.filter((c) => c.candidateId !== candidateId);

						if (filteredCandidates.length === 1) {
							filteredCandidates[0].isPrimary = true;
						}

						const addRemoveOb = candidateRating?.addRemoveOb ?? [];
						const filteredAddRemoveOb = addRemoveOb.filter((c) => c.candidateId !== candidateId);

						const otherComments = candidateRating?.otherComments ?? [];
						const filteredOtherComments = otherComments.filter((c) => c.candidateId !== candidateId);

						const selectedOBs = candidateRating?.selectedOBs ?? [];
						const filteredSelectedOBs = selectedOBs.filter((c) => c.candidateId !== candidateId);

						const temEvaluations = candidateRating?.temEvaluations ?? [];
						const filteredTemEvaluations = temEvaluations.filter((c) => c.candidateId !== candidateId);

						const temRate = candidateRating?.temRate ?? {};
						const filteredTemRate = temRate;
						for (const id of assessmentIds) {
							delete filteredTemRate[`${candidateId}.${id}`];
						}

						const resetManeuvers = candidateRating?.resetManeuvers ?? [];
						const filteredResetManeuvers = resetManeuvers.filter((r) => r.candidateId !== candidateId);

						const candidateSignOffDetails = signOffDetails?.candidateSignOffDetails ?? {};
						const filteredCandidateSignOffDetails = candidateSignOffDetails;
						delete filteredCandidateSignOffDetails[candidateId];

						this.updateSelectedDraft({
							trainingInformation: {
								...trainingInfo,
								candidates: filteredCandidates,
							},
							candidateRating: {
								...candidateRating,
								addRemoveOb: filteredAddRemoveOb,
								otherComments: filteredOtherComments,
								selectedOBs: filteredSelectedOBs,
								temEvaluations: filteredTemEvaluations,
								temRate: filteredTemRate,
								resetManeuvers: filteredResetManeuvers,
							},
							signOffDetails: {
								candidateSignOffDetails: filteredCandidateSignOffDetails,
							},
							lastUpdatedDate: new Date().toISOString(),
							continuePage: 1,
						});
					}
				});
			}
		}
	}

	onSaveToDraftsSuccess(toList: boolean = false): void {
		const dialogRef = this.dialog.open(CbtaModalComponent, {
			panelClass: "save-to-drafts-success-modal",
			height: "200px",
			width: "360px",
			hasBackdrop: true,
			data: {
				modalTitle: "Successful",
				modalContent: "Assessment saved to draft successfully!",
				modalBtnConfirmText: "Close",
				noCloseBtn: true,
			},
		});

		const sub = dialogRef.afterClosed().subscribe((result: any) => {
			if (toList) {
				this.router.navigate([RouteType.PENDING]);
			}
			sub.unsubscribe();
		});
	}

	blockSubmitIfOffline(toList: boolean = false): void {
		const dialogRef = this.dialog.open(CbtaModalComponent, {
			panelClass: "submit-offline-modal",
			height: "240px",
			width: "360px",
			hasBackdrop: true,
			data: {
				modalTitle: "Failed",
				modalContent:
					'You can only proceed to sign off when you are connected to the internet. Please proceed to "Save as draft" and sign off when you are connected to the internet.',
				modalBtnConfirmText: "Save As Draft",
				noCloseBtn: true,
			},
		});

		const sub = dialogRef.afterClosed().subscribe((result: any) => {
			this.updateSelectedDraft({ continuePage: 3 });
			this.onSaveToDraftsSuccess(toList);
			sub.unsubscribe();
		});
	}

	private newDataBody(sequenceNumber: number, eventName: string, body: any): DraftBody {
		return {
			sequenceNumber,
			eventName,
			body,
		};
	}

	addToBeSynched(eventName: string, body: any) {
		const data = {
			eventName,
			body,
		};

		const toBeSynced = this._draft?.toBeSynced ?? [];

		toBeSynced.push(data);

		this.updateSelectedDraft({
			toBeSynced: toBeSynced,
		});
	}

	syncSelectedDraft() {
		if (!navigator.onLine) {
			return;
		}

		if (this._draft) {
			const draftId = this._draft.id;

			if (draftId) {
				this.dbService.getByID<DraftItem>(CONSTANT.INDEXEDDB.DRAFT, draftId).subscribe(
					(record) => {
						const formDetails = record?.formDetails;
						const trainingInfo = record?.trainingInformation;
						const signOffDetails = record?.signOffDetails;
						const synced = record?.synced ?? false;
						const assessmentDate = moment(trainingInfo?.assessmentDate).format("YYYY-MM-DD");
						const toBeSynced = record?.toBeSynced ?? [];

						const candidates = trainingInfo?.candidates ?? [];

						const dataBody: any[] = [];
						let sequenceNumber = 0;

						if (!synced) {
							const candidateDetails = this.newDataBody(sequenceNumber, "candidate", candidates);

							dataBody.push(candidateDetails);
							sequenceNumber += 1;

							const flightDetails = this.newDataBody(sequenceNumber, "flight_sim", {
								simReg: trainingInfo?.simReg,
								airportRoute: trainingInfo?.airportRoute,
								accumulatedSectors: trainingInfo?.accumulatedSectors,
								acType: trainingInfo?.acType,
								sectorNumber: trainingInfo?.sectorNumber,
							});

							dataBody.push(flightDetails);
							sequenceNumber += 1;
						}

						let newSignOffDetails: any = {};

						if (signOffDetails) {
							const candidateSignOffDetails = signOffDetails?.candidateSignOffDetails ?? {};

							for (const candidateId in candidateSignOffDetails) {
								const details = candidateSignOffDetails[candidateId];

								const detailsSynced = details?.synced ?? false;
								const is_complete = details?.sessionCheck;
								const is_clear = details?.clearanceCheck ?? "N/A";
								const is_obj_met = details?.objectiveCheck;

								newSignOffDetails[candidateId] = newSignOffDetails[candidateId] || {
									...details,
								};

								// to sync only valid recommendations, so would not cause error, and only synced once
								if (is_complete && is_obj_met && !detailsSynced) {
									const recommendations = this.newDataBody(sequenceNumber, "recommendation", {
										candidateId,
										sessionType: details?.category?.toLowerCase(),
										is_complete,
										is_clear,
										is_obj_met,
									});

									newSignOffDetails[candidateId].synced = true;

									dataBody.push(recommendations);
									sequenceNumber += 1;
								} else {
									console.log(`${candidateId}: recommendations not valid or already synced`);
								}
							}
						}

						for (const t of toBeSynced) {
							dataBody.push(t);
							sequenceNumber += 1;
						}

						this.httpService
							.syncDraft({
								traineeAssessmentId: record.id,
								instructorId: trainingInfo?.instructorId,
								formId: formDetails?.id,
								assessmentDate,
								data: dataBody,
							})
							.subscribe(
								(result) => {
									console.log(result);
									this.calculateRating(record.id, candidates);
									this.updateSelectedDraft({
										signOffDetails: {
											candidateSignOffDetails: newSignOffDetails,
										},
										synced: true,
										// clear to be synched
										toBeSynced: [],
									});
								},
								(error) => {
									console.error("sync failed", error);
								},
							);
					},
					(error) => {
						console.error(error);
					},
				);
			}
		}
	}

	calculateRating(id: string, candidates: RatingCandidate[]) {
		for (const candidate of candidates) {
			const candidateId = candidate.candidateId;

			if (candidateId) {
				this.httpService.calculateRating(id, candidateId).subscribe(
					(resp: any) => {
						const signOffDetails = this.draft?.signOffDetails;

						this.updateSelectedDraft({
							signOffDetails: {
								candidateSignOffDetails: {
									...signOffDetails?.candidateSignOffDetails,
									[candidateId]: {
										...signOffDetails?.candidateSignOffDetails[candidateId],
										...resp.data,
									},
								},
							},
							continuePage: 3,
						});
					},
					(error) => {
						console.error(`calculation error ${candidateId}`, error);
					},
				);
			} else {
				console.error(`undefined id:`, candidateId);
			}
		}
	}

	clearDraft() {
		this.inDraftMode = false;
		this._draft = undefined;
	}

	get draft(): DraftItem | undefined {
		return this._draft;
	}
}
